import { useState, useMemo, useCallback, Suspense } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { WelcomeContext, type WelcomeContextValue, type OnboardingFlowVersionStep } from '../../welcome/WelcomeContext';
import {
  STEP_COMPONENT_REGISTRY,
  preloadAllSteps,
  STEP_MAX_WIDTHS,
  DEFAULT_STEP_MAX_WIDTH,
  findFinaleStepCode,
  isCompletionStep,
} from '../../welcome/step-registry';
import Done from '../../welcome/steps/Done';
import StepDots from '../../welcome/components/StepDots';
import '../../welcome/welcome.css';
import type { EditorStep, RegistryEntry } from './types';

interface PreviewModalProps {
  steps: EditorStep[];
  registryByCode: Map<string, RegistryEntry>;
  initialStepIndex: number;
  onClose: () => void;
}

preloadAllSteps();

export function PreviewModal({ steps, registryByCode, initialStepIndex, onClose }: PreviewModalProps) {
  const { t } = useTranslation('welcome-onboarding');
  const enabledSteps = useMemo(() => steps.filter((s) => s.isEnabled), [steps]);
  // -1 = intro, 0..N-1 = steps, N = classic Done.
  // A COMPLETION_* ending rests on its last step — WelcomeWizard never swaps in Done.
  const endsOnCompletion = isCompletionStep(enabledSteps[enabledSteps.length - 1]?.stepCode);
  const maxIndex = endsOnCompletion ? Math.max(enabledSteps.length - 1, -1) : enabledSteps.length;
  const [index, setIndex] = useState(() => Math.max(-1, Math.min(initialStepIndex, maxIndex)));

  const mockSteps = useMemo<OnboardingFlowVersionStep[]>(
    () =>
      enabledSteps.map((s, i) => {
        const reg = registryByCode.get(s.stepCode);
        return {
          id: s.stepCode,
          stepCode: s.stepCode,
          position: i + 1,
          isEnabled: true,
          configOverrides: s.configOverrides,
          config: { ...(reg?.defaultConfig ?? {}), ...s.configOverrides },
          isRequired: s.required,
          description: s.description,
          rationale: s.rationale,
        };
      }),
    [enabledSteps, registryByCode]
  );

  const finaleCode = findFinaleStepCode(mockSteps.map((step) => step.stepCode));
  const finaleStep = finaleCode ? (mockSteps.find((step) => step.stepCode === finaleCode) ?? null) : null;
  // Done index is past the last step, so `mockSteps[index]` is undefined. When the version
  // ends on a finale, keep that step as currentStep so the remounted shell still reads
  // the author's overrides instead of falling back to default copy / Hava.
  const currentStep = (index === mockSteps.length && finaleStep ? finaleStep : mockSteps[index]) ?? null;

  const advance = useCallback(() => {
    setIndex((i) => Math.min(i + 1, maxIndex));
  }, [maxIndex]);

  const retreat = useCallback(() => {
    setIndex((i) => Math.max(i - 1, -1));
  }, []);

  const mockContextValue = useMemo<WelcomeContextValue>(() => {
    return {
      session: {
        id: 'preview',
        currentStepCode: currentStep?.stepCode ?? '',
        completedRequiredCount: 0,
        completedTotalCount: 0,
        outcome: 'IN_PROGRESS',
        startedAt: new Date().toISOString(),
        lastActiveAt: new Date().toISOString(),
        flowVersion: {
          id: 'preview',
          version: 0,
          name: 'Preview',
          isDefault: false,
          steps: mockSteps,
        },
      },
      loading: false,
      data: {},
      updateData: () => {},
      // Preview never navigates (isPreview short-circuits FinaleShell), so there is no
      // session outcome to report back.
      submitStep: async () => {
        advance();
        return null;
      },
      // Quiet-submit callers advance via refreshSession in the real wizard; in the
      // preview that's the same forward tap.
      refreshSession: async () => advance(),
      skipStep: async () => advance(),
      goBack: async () => retreat(),
      currentStep,
      steps: mockSteps,
      stepIndex: index,
      isAdvancing: false,
      arrivedViaBack: false,
      isPreview: true,
    };
  }, [currentStep, mockSteps, index, advance, retreat]);

  const StepComponent = currentStep
    ? STEP_COMPONENT_REGISTRY[currentStep.stepCode as keyof typeof STEP_COMPONENT_REGISTRY]
    : null;

  const isIntro = index === -1;
  const isDone = index === mockSteps.length;
  const canGoPrev = index > -1;
  const canGoNext = index < maxIndex;
  const screenCount = maxIndex + 2;

  let headerLabel: string;
  if (isIntro) {
    headerLabel = 'INTRO';
  } else if (isDone) {
    headerLabel = 'DONE';
  } else {
    headerLabel = currentStep?.stepCode ?? '—';
  }

  let counterLabel: string;
  if (isIntro) {
    counterLabel = `intro / ${screenCount}`;
  } else if (isDone) {
    counterLabel = `done / ${screenCount}`;
  } else {
    counterLabel = `${index + 2} / ${screenCount}`;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label="Onboarding flow preview"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-4 bg-white border-b border-gray-200 px-4 py-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-medium text-gray-900">Preview</span>
          <span className="h-4 w-px bg-gray-300" aria-hidden />
          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 rounded font-mono">
            {headerLabel}
          </span>
          <span className="text-xs text-gray-400 tabular-nums">{counterLabel}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          aria-label="Close preview"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Step area */}
      <div className="flex-1 overflow-y-auto relative" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Left nav button */}
        <button
          type="button"
          onClick={() => setIndex((i) => Math.max(i - 1, -1))}
          disabled={!canGoPrev}
          aria-label="Previous step"
          className="fixed left-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-25 disabled:cursor-not-allowed transition-opacity"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        {/* Right nav button */}
        <button
          type="button"
          onClick={() => setIndex((i) => Math.min(i + 1, maxIndex))}
          disabled={!canGoNext}
          aria-label="Next step"
          className="fixed right-4 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-25 disabled:cursor-not-allowed transition-opacity"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        <WelcomeContext.Provider value={mockContextValue}>
          <div className="ob-root" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {!isIntro && !isDone && <StepDots total={mockSteps.length} currentIndex={index} />}

            {isIntro ? (
              <div className="ob-step-container">
                <div className="ob-welcome-intro" style={{ width: '100%', maxWidth: '720px' }}>
                  <div className="ob-welcome-intro-bg" aria-hidden />
                  <div className="ob-welcome-intro-eyebrow">
                    <span>{t('intro.eyebrow')}</span>
                  </div>
                  <h1 className="ob-welcome-intro-title">
                    <Trans i18nKey="intro.title" t={t} components={{ em: <em /> }} />
                  </h1>
                  <p className="ob-welcome-intro-subtitle">{t('intro.subtitle')}</p>
                  <button
                    type="button"
                    className="ob-welcome-intro-cta"
                    onClick={() => setIndex((i) => Math.min(i + 1, maxIndex))}
                  >
                    <span className="ob-welcome-intro-cta-stack">
                      <span className="ob-welcome-intro-cta-main">{t('intro.cta')}</span>
                      <span className="ob-welcome-intro-cta-sub">{t('intro.cta_sub')}</span>
                    </span>
                    <span className="ob-welcome-intro-cta-arrow" aria-hidden>
                      →
                    </span>
                  </button>
                  <div className="ob-welcome-intro-strip">
                    {[
                      'intro.feature_feedback',
                      'intro.feature_learn',
                      'intro.feature_words',
                      'intro.feature_artists',
                    ].map((key) => (
                      <span key={key} className="ob-welcome-intro-tag">
                        <span className="ob-welcome-intro-tick" aria-hidden>
                          ✓
                        </span>
                        {t(key)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : isDone ? (
              /* Past the last step only for a classic ending. A FINALE_* version previews
                 that step. COMPLETION_* versions never reach this index — the last
                 completion step is the resting UI, same as WelcomeWizard. */
              (() => {
                const FinaleComponent = finaleCode
                  ? STEP_COMPONENT_REGISTRY[finaleCode as keyof typeof STEP_COMPONENT_REGISTRY]
                  : null;
                if (!FinaleComponent) {
                  return <Done />;
                }
                return (
                  <div className="ob-step-container">
                    <div
                      style={{
                        width: '100%',
                        maxWidth: STEP_MAX_WIDTHS[finaleCode ?? ''] ?? DEFAULT_STEP_MAX_WIDTH,
                      }}
                    >
                      <Suspense fallback={null}>
                        <FinaleComponent />
                      </Suspense>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="ob-step-container">
                <div
                  style={{
                    width: '100%',
                    maxWidth: STEP_MAX_WIDTHS[currentStep?.stepCode ?? ''] ?? DEFAULT_STEP_MAX_WIDTH,
                  }}
                >
                  <Suspense
                    fallback={
                      <div
                        className="ob-step-card"
                        style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <span style={{ color: 'var(--ob-gray-400)', fontSize: '14px' }}>Loading step…</span>
                      </div>
                    }
                  >
                    {StepComponent ? (
                      <StepComponent />
                    ) : (
                      <div className="ob-step-card">
                        <p style={{ color: 'var(--ob-gray-600)', fontSize: '14px' }}>
                          No component registered for <code>{currentStep?.stepCode}</code>.
                        </p>
                      </div>
                    )}
                  </Suspense>
                </div>
              </div>
            )}
          </div>
        </WelcomeContext.Provider>
      </div>
    </div>
  );
}
